import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash, FloppyDisk, Info } from '@phosphor-icons/react';
import { useTenantPath } from '../../lib/tenantPath';
import { API, inr } from './_shared';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * VP+ create-cycle form.
 *
 * Form is intentionally single-page (no wizard) so the founder can bang out a
 * cycle in under a minute. Layout is:
 *   [Basic info: title, frequency, review day, commitment window]
 *   [Members table: pick user + set target ₹]
 *   [Notes]
 *   [Submit]
 *
 * On submit, backend auto-creates the first period, so members see the cycle
 * immediately in their /reviews landing.
 */
export default function CreateCycle() {
  const nav = useNavigate();
  const tp = useTenantPath();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: '',
    frequency: 'weekly',
    review_day_of_week: 4,   // Friday
    review_day_of_month: 1,
    review_month_of_quarter: 3,  // last month of quarter
    commitment_window_hours: 24,
    notes: '',
    members: [],
    // iter114 — Optional single deputy who oversees the cycle (approve
    // + close powers, appears in their Reviews tab as OVERSEEING).
    in_charge_user_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // /goals/assignable-users already returns only approvable users — no need
    // to re-filter by status (that field isn't present in the response, so a
    // status filter would silently drop everyone and the picker would be empty).
    axios.get(`${API}/goals/assignable-users`)
      .then((r) => setUsers(r.data.users || r.data || []))
      .catch(() => setUsers([]));
  }, []);

  const availableUsers = useMemo(() => {
    const picked = new Set(form.members.map((m) => m.user_id));
    return users.filter((u) => !picked.has(u.id));
  }, [users, form.members]);

  const addMember = (uid) => {
    const u = users.find((x) => x.id === uid);
    if (!u) return;
    setForm((f) => ({
      ...f,
      members: [...f.members, { user_id: u.id, user_name: u.name, user_role: u.role, target_value: 0 }],
    }));
  };
  const rmMember = (uid) => setForm((f) => ({ ...f, members: f.members.filter((m) => m.user_id !== uid) }));
  const setTarget = (uid, v) => setForm((f) => ({
    ...f, members: f.members.map((m) => m.user_id === uid ? { ...m, target_value: Number(v) || 0 } : m),
  }));

  const totalTargets = form.members.reduce((a, m) => a + (m.target_value || 0), 0);

  const canSubmit =
    form.title.trim().length > 0 &&
    form.members.length > 0 &&
    form.members.every((m) => m.target_value > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/reviews/cycles`, form);
      nav(tp(`/reviews/cycles/${r.data.cycle.id}`));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create cycle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="create-cycle-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#6D8E87] hover:text-[#00a69c] inline-flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={14} /> Back
      </button>
      <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>New Review Cycle</h1>
      <p className="text-sm text-[#6D8E87] mb-6">Set the rhythm. The first period will be auto-generated for the next review day.</p>

      {/* Basic info card */}
      <div className="medical-card p-5 space-y-4 mb-4">
        <div>
          <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Cycle title *</label>
          <input
            type="text" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Weekly Sales Commitment Review"
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
            data-testid="cycle-title-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Frequency *</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
              data-testid="cycle-frequency-select"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (Indian FY)</option>
            </select>
          </div>

          {form.frequency === 'weekly' && (
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Review day *</label>
              <div className="flex gap-1" data-testid="cycle-dow-picker">
                {DOW.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => setForm({ ...form, review_day_of_week: i })}
                    className={`flex-1 px-2 py-2 text-xs font-semibold rounded border transition ${
                      form.review_day_of_week === i
                        ? 'bg-[#00a69c] text-white border-[#00a69c]'
                        : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'
                    }`}
                    data-testid={`dow-${d.toLowerCase()}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {form.frequency === 'monthly' && (
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Day of month *</label>
              <input
                type="number" min={1} max={28}
                value={form.review_day_of_month}
                onChange={(e) => setForm({ ...form, review_day_of_month: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                data-testid="cycle-dom-input"
              />
              <p className="text-[10px] text-[#6D8E87] mt-1">1–28 (Feb-safe)</p>
            </div>
          )}

          {form.frequency === 'quarterly' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Month of quarter *</label>
                <select
                  value={form.review_month_of_quarter}
                  onChange={(e) => setForm({ ...form, review_month_of_quarter: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
                  data-testid="cycle-moq-select"
                >
                  <option value={1}>1st month</option>
                  <option value={2}>2nd month</option>
                  <option value={3}>3rd (last) month</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Day of month *</label>
                <input
                  type="number" min={1} max={28}
                  value={form.review_day_of_month}
                  onChange={(e) => setForm({ ...form, review_day_of_month: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#3B5A54] mb-1">
            Commitment window (hours before review) *
          </label>
          <input
            type="number" min={1} max={168}
            value={form.commitment_window_hours}
            onChange={(e) => setForm({ ...form, commitment_window_hours: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
            data-testid="cycle-window-input"
          />
          <p className="text-[10px] text-[#6D8E87] mt-1 flex items-center gap-1">
            <Info size={10} /> 24 = "one day before". Members can commit any time in this window before the review-day cutoff.
          </p>
        </div>
      </div>

      {/* Members */}
      <div className="medical-card p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Members & Targets</h2>
          {form.members.length > 0 && (
            <span className="text-xs text-[#6D8E87]">
              {form.members.length} member{form.members.length === 1 ? '' : 's'} · Total target <b className="text-[#00a69c]">{inr(totalTargets)}</b>
            </span>
          )}
        </div>

        {form.members.length === 0 && (
          <p className="text-sm text-[#6D8E87] italic mb-3">No members yet — add at least one below.</p>
        )}

        {form.members.map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 mb-2 p-2 bg-[#F4F9F7] rounded-lg" data-testid={`member-row-${m.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#052A23] truncate">{m.user_name}</div>
              <div className="text-xs text-[#6D8E87]">{m.user_role}</div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-[#6D8E87]">₹</span>
              <input
                type="number" min={0} step={1000}
                value={m.target_value}
                onChange={(e) => setTarget(m.user_id, e.target.value)}
                placeholder="Target"
                className="w-32 px-2 py-1 border border-[#D5E8E4] rounded text-sm text-right"
                data-testid={`member-target-${m.user_id}`}
              />
            </div>
            <button
              onClick={() => rmMember(m.user_id)}
              className="w-7 h-7 rounded border border-red-200 text-red-500 hover:bg-red-50 grid place-items-center"
              data-testid={`member-remove-${m.user_id}`}
            >
              <Trash size={12} />
            </button>
          </div>
        ))}

        {availableUsers.length > 0 && (
          <div className="mt-3 flex gap-2">
            <select
              onChange={(e) => { if (e.target.value) { addMember(e.target.value); e.target.value = ''; } }}
              className="flex-1 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
              data-testid="add-member-select"
              value=""
            >
              <option value="">+ Add member…</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
              ))}
            </select>
          </div>
        )}

        {/* iter114 — In-charge section. Single-select. Optional. Can be
            anyone (RSM overseeing a region is the canonical use case).
            The picked user does NOT have to also be a member — they can
            purely oversee without a target. If they ARE also a member,
            their committing role wins for target/won calculations. */}
        <div className="mt-6 pt-5 border-t border-[#E2F3F0]">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              In-charge <span className="text-xs font-normal text-[#6D8E87]">(optional — deputised for approve + close)</span>
            </h3>
            {form.in_charge_user_id && (
              <button
                onClick={() => setForm((f) => ({ ...f, in_charge_user_id: '' }))}
                className="text-[10px] uppercase tracking-widest text-red-500 hover:text-red-700 font-bold"
                data-testid="clear-in-charge-btn"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-xs text-[#6D8E87] mb-2">
            The person you pick will see this cycle in their Reviews tab as <b>Overseeing</b>, and can approve under-commits + close periods for this cycle only. Notifications fire on period rollover + missed deadlines.
          </p>
          <select
            value={form.in_charge_user_id}
            onChange={(e) => setForm({ ...form, in_charge_user_id: e.target.value })}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
            data-testid="in-charge-select"
          >
            <option value="">— No in-charge —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="medical-card p-5 mb-4">
        <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Notes (optional)</label>
        <textarea
          rows={3} value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Context or rules for this cycle — visible to all members."
          className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm resize-none"
          data-testid="cycle-notes-input"
        />
      </div>

      {error && <div className="medical-card p-4 border-l-4 border-red-500 text-sm text-red-700 mb-4" data-testid="submit-error">{error}</div>}

      <div className="flex justify-end gap-2">
        <button onClick={() => nav(-1)} className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]" data-testid="cancel-btn">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
          data-testid="submit-cycle-btn"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? 'Creating…' : 'Create cycle'}
        </button>
      </div>
    </div>
  );
}
